import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation, useRevalidator } from "react-router-dom";
import Link from "../../../../components/Link";
import {
  Box, Button,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead, Tooltip,
  Tr,
  Badge,
  Icon,
  useColorModeValue,
  usePrefersReducedMotion,
  HStack,
  VStack,
  Container,
  Select,
  Avatar,
  useBreakpointValue,
  Card,
  CardBody,
  SimpleGrid,
  Divider
} from "@chakra-ui/react";

import { ArrowRightIcon, TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { FaTicketAlt, FaFire, FaBolt, FaClock, FaCalendarAlt, FaFilter, FaUser } from "react-icons/fa";
import { TicketState } from "../../../../components/States";
import { translateTableBadge } from "../../../../shared/libs/translate";
import urlToId from "../../../../helpers/UrlToId";

import formatDate from "../../../../helpers/formatDate";
import formatDateTable from "../../../../helpers/FormatDateTable";
import ShortenedText from "../../../../components/ShortenedText";
import Pagination from "../../../../components/pagination/Pagination";
import ExportButton from "../../../../components/export/ExportButton";
import { useTranslation } from 'react-i18next';
import AuthContext from "../../../../contexts/Auth";



export default function TicketListTableView({ includeFilters, tickets }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();
  const auth = useContext(AuthContext);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return window.localStorage.getItem('settings.autoRefreshTickets') === 'true'; } catch (_) { return false; }
  });
  const [countdown, setCountdown] = useState(() => {
    try { return parseInt(window.localStorage.getItem('settings.autoRefreshInterval') || '5', 10); } catch (_) { return 5; }
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [viewMode] = useState("table");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    title: true,
    customer: true,
    employee: false,
    state: true,
    reseller: false,
    rating: false,
    created_at: true,
    updated_at: false,
    actions: false
  });

  // Color scheme
  const headerBg = useColorModeValue(
    "linear(135deg, var(--chakra-colors-red-600) 0%, var(--chakra-colors-red-800) 100%)",
    "linear(135deg, var(--chakra-colors-red-500) 0%, var(--chakra-colors-red-700) 100%)"
  );

  
  const cardBg = useColorModeValue(
    "white",
    "gray.800"
  );
  
  const tableBg = useColorModeValue(
    "gray.50",
    "gray.700"
  );
  const lowDataMode = (typeof window !== 'undefined' && window.localStorage.getItem('settings.lowDataMode') === 'true');
  
  const borderColor = useColorModeValue(
    "gray.200",
    "gray.600"
  );
  
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const mobileCardTitleColor = useColorModeValue("gray.800", "white");
  const mobileCardSubtitleColor = useColorModeValue("gray.600", "gray.300");
  const pageBg = useColorModeValue(
    "linear(135deg, var(--chakra-colors-gray-50) 0%, var(--chakra-colors-gray-100) 100%)",
    "linear(135deg, var(--chakra-colors-gray-900) 0%, var(--chakra-colors-gray-800) 100%)"
  );
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Sync filters with URL parameters whenever location changes
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlStatus = urlParams.get('state');
    const urlNumber = urlParams.get('number');
    const urlQ = urlParams.get('q');
    const urlKind = urlParams.get('kind');
    const urlPriority = urlParams.get('priority');
    const urlCity = urlParams.get('city');
    const urlDistrict = urlParams.get('district');
    
    if (urlQ) {
      setSearchTerm(urlQ);
    } else if (urlNumber) {
      setSearchTerm(urlNumber);
    }
    if (urlKind) {
      setKindFilter(urlKind);
    }
    if (urlPriority) {
      setPriorityFilter(urlPriority);
    }
    if (urlCity) {
      setCityFilter(urlCity);
    }
    if (urlDistrict) {
      setDistrictFilter(urlDistrict);
    }
    
    if (urlStatus) {
      // Map API state values back to dropdown values
      if (urlStatus === 'new,assignment') {
        setStatusFilter('new');
      } else if (urlStatus === 'in_progress,in_queue,eval_started') {
        setStatusFilter('in_progress');
      } else if (urlStatus.includes('in_progress,in_queue,eval_started,approval')) {
        setStatusFilter('assigned');
      } else if (['completed', 'cancelled'].includes(urlStatus)) {
        setStatusFilter(urlStatus);
      } else {
        setStatusFilter('all');
      }
    }
  }, [location.search]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('settings.list.visibleColumns');
      if (raw) {
        const arr = JSON.parse(raw);
        const next = { ...visibleColumns };
        Object.keys(next).forEach(k => { next[k] = arr.includes(k); });
        setVisibleColumns(next);
      }
    } catch (_) {}
    try {
      const ds = window.localStorage.getItem('settings.list.defaultSort') || 'created_at:desc';
      const [f, d] = ds.split(':');
      if (f) setSortField(f);
      if (d) setSortDirection(d);
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const remember = window.localStorage.getItem('settings.list.rememberFilters') === 'true';
      if (!remember) return;
      const sp = new URLSearchParams(location.search);
      const onlyPage = sp.keys && Array.from(sp.keys()).every(k => k === 'page');
      if (!sp.toString() || onlyPage) {
        const raw = window.localStorage.getItem('lastTicketFilters');
        if (raw) {
          const f = JSON.parse(raw);
          const url = new URL(location.pathname + location.search, window.location.origin);
          url.searchParams.set('page', '1');
          if (f.status) url.searchParams.set('state', f.status === 'all' ? '' : f.status);
          if (f.kind) url.searchParams.set('kind', f.kind);
          if (f.priority) url.searchParams.set('priority', f.priority);
          if (f.city) url.searchParams.set('city', f.city);
          if (f.district) url.searchParams.set('district', f.district);
          navigate(url.pathname + '?' + url.searchParams.toString(), { replace: true });
        }
      }
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false });
  const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const cardSpacing = useBreakpointValue({ base: 4, md: 6, lg: 8 });

  React.useEffect(() => {
    const loadCities = async () => {
      try {
        const res = await fetch('/public/cities/');
        if (res.ok) {
          const data = await res.json();
          const results = Array.isArray(data?.results) ? data.results : [];
          setCities(results);
        }
      } catch (e) {}
    };
    loadCities();
  }, []);

  React.useEffect(() => {
    const loadDistricts = async () => {
      try {
        const url = new URL('/public/districts/', window.location.origin);
        if (cityFilter) {
          url.searchParams.set('city', cityFilter);
        }
        const res = await fetch(url.toString());
        if (res.ok) {
          const data = await res.json();
          const results = Array.isArray(data?.results) ? data.results : [];
          setDistricts(results);
        }
      } catch (e) {}
    };
    loadDistricts();
  }, [cityFilter]);

  // export helpers moved below filteredAndSortedTickets to avoid TDZ
  
  // Auto-hide columns on smaller screens
  const getResponsiveColumns = () => {
    const baseColumns = [
      { key: "id", label: "#", width: "80px", sortable: true, priority: 1 },
      { key: "title", label: t('ticket.header.title') || 'Başlık', width: "300px", sortable: true, priority: 1 },
      { key: "customer", label: t('ticket.header.customer_label') || 'Müşteri', width: "200px", sortable: true, priority: 2 },
      { key: "state", label: t('ticket.header.status_label') || 'Durum', width: "180px", sortable: true, priority: 2 },
      { key: "reseller", label: t('ticket.header.reseller_label') || 'Bayi', width: "200px", sortable: true, priority: 2 },
      { key: "created_at", label: t('ticket.header.created_label') || 'Oluşturulma', width: "150px", sortable: true, priority: 3 },
      // { key: "actions", label: "İşlemler", width: "120px", sortable: false, priority: 1 }
    ];
    
    if (isMobile) {
      // Mobile: Show only essential columns
      return baseColumns.filter(col => col.priority <= 2 && visibleColumns[col.key]);
    } else if (isTablet) {
      // Tablet: Show priority 1-3 columns
      return baseColumns.filter(col => col.priority <= 3 && visibleColumns[col.key]);
    } else {
      // Desktop: Show all visible columns
      return baseColumns.filter(col => visibleColumns[col.key]);
    }
  };
  
  const columns = getResponsiveColumns();
  
  


  // Filter and sort tickets
  const filteredAndSortedTickets = React.useMemo(() => {
    const ticketData = tickets?.results && Array.isArray(tickets.results) ? tickets.results : [];
    if (!ticketData || ticketData.length === 0) {
      return [];
    }
    
    // For numeric search, API handles it via 'number' parameter
    // For non-numeric search, we need to filter locally since API doesn't support general text search
    let filtered = ticketData.filter(ticket => {
      // Only apply local search if search term is non-numeric
      if (searchTerm && searchTerm.trim() && !/^\d+$/.test(searchTerm.trim())) {
        const matchesSearch = (ticket.title && ticket.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (ticket.full_name && ticket.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (ticket.customer?.name && ticket.customer.name.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesSearch;
      }
      return true;
    });
    
    // Sort tickets
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === "customer") {
        aValue = a.full_name || a.customer?.name || "";
        bValue = b.full_name || b.customer?.name || "";
      }
      
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }
      
      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [tickets, searchTerm, sortField, sortDirection]);

  const exportTicketsData = React.useMemo(() => {
    const list = Array.isArray(filteredAndSortedTickets) ? filteredAndSortedTickets : [];
    return list.map((item) => ({
      [t('tickets.number') || 'Numara']: item.number,
      [t('ticket.header.title') || 'Başlık']: item.title,
      [t('ticket.header.customer_label') || 'Müşteri']: item.full_name || item.customer?.name || "",
      [t('ticket.header.status_label') || 'Durum']: item.state,
      [t('ticket.header.created_label') || 'Oluşturulma']: formatDateTable(item.created_at),
      [t('ticket.timeline.last_update') || 'Güncellenme']: formatDateTable(item.updated_at),
    }));
  }, [filteredAndSortedTickets, t]);

  const ticketExportColumns = React.useMemo(() => ([
    { key: t('tickets.number') || 'Numara', label: t('tickets.number') || 'Numara' },
    { key: t('ticket.header.title') || 'Başlık', label: t('ticket.header.title') || 'Başlık' },
    { key: t('ticket.header.customer_label') || 'Müşteri', label: t('ticket.header.customer_label') || 'Müşteri' },
    { key: t('ticket.header.status_label') || 'Durum', label: t('ticket.header.status_label') || 'Durum' },
    { key: t('ticket.header.created_label') || 'Oluşturulma', label: t('ticket.header.created_label') || 'Oluşturulma' },
    { key: t('ticket.timeline.last_update') || 'Güncellenme', label: t('ticket.timeline.last_update') || 'Güncellenme' },
  ]), [t]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    try { window.localStorage.setItem('settings.list.defaultSort', `${field}:${sortDirection === 'asc' ? 'desc' : 'asc'}`); } catch (_) {}
  };

  const getStatusColor = (state) => {
    switch(state) {
      case "new": return "blue";
      case "assigned": return "yellow";
      case "in_progress": return "orange";
      case "completed": return "green";
      case "cancelled": return "red";
      default: return "gray";
    }
  };

  useEffect(() => {
    let interval;
    if (autoRefresh && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            revalidator.revalidate();
            return 5;
          }
          return prev - 1;
        });
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, countdown, revalidator]);

  // WebSocket: listen for dashboard updates and refresh tickets list when needed
  useEffect(() => {
    try {
      const base = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/^http/, 'ws').replace(/\/$/, '');
      let resellerId = undefined;
      try {
        const user = auth?.data?.user;
        const resellerUrl = user?.employee?.reseller?.url;
        resellerId = resellerUrl ? urlToId(resellerUrl) : undefined;
      } catch (_) {}
      const wsUrl = base + "/ws/dashboard/" + (resellerId ? `?reseller=${resellerId}` : "");
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data?.event === 'resource' && data?.resource === 'tickets') {
            if (!data?.reseller || data.reseller === 'all' || (resellerId && String(data.reseller) === String(resellerId))) {
              revalidator.revalidate();
            }
          }
        } catch (_) {}
      };
      return () => { try { ws.close(); } catch (_) {} };
    } catch (_) {}
  }, [auth, revalidator]);

  return (
    <Box
        minH="120vh"
        bgGradient={pageBg}
        position="relative"
        overflow="hidden"
      >

        
        <Box position="relative" zIndex={2} p={containerPadding}>
          <Container maxW={isMobile ? "full" : "8xl"} px={isMobile ? 2 : 8}>
            <VStack spacing={cardSpacing} align="stretch">
              
              {/* Header */}
              <Box
                bgGradient={headerBg}
                borderRadius="xl"
                p={6}
                mb={6}
                shadow="md"
                color="white"
               >
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={3}>
                      <Icon as={FaTicketAlt} boxSize={8} color="white" />
                      <Text fontSize="3xl" fontWeight="700" color="white">
                        {t('tickets.create_new_service_request') || 'Ticket Yönetimi'}
                      </Text>
                    </HStack>
                    <HStack spacing={6}>
                      <HStack spacing={2}>
                        <Icon as={FaTicketAlt} boxSize={4} />
                        <Text fontSize="md" opacity={0.9}>
                          {filteredAndSortedTickets.length} / {tickets?.num_of_items || 0} {t('tickets.create_ticket') || 'Ticket'}
                        </Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Icon as={FaClock} boxSize={4} />
                        <Text fontSize="md" opacity={0.9}>
                          {t('ticket.statistics.last_update')}: {new Date().toLocaleTimeString('tr-TR')}
                        </Text>
                      </HStack>
                    </HStack>
                  </VStack>

                  <HStack spacing={3}>
                    <ExportButton
                      data={exportTicketsData}
                      filename="tickets"
                      columns={ticketExportColumns}
                      sheetName={t('tickets.create_ticket') || 'Tickets'}
                    />
                    <Button
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      size="md"
                      variant="outline"
                      colorScheme="whiteAlpha"
                      color="white"
                      _hover={{
                        bg: "rgba(255, 255, 255, 0.1)"
                      }}
                    >
                      <HStack spacing={2}>
                        <Icon as={autoRefresh ? FaBolt : FaFire} boxSize={5} />
                          <Text>
                          {autoRefresh ? `${t('common.refresh')} (${countdown}dk)` : t('common.refresh')}
                          </Text>
                        </HStack>
                    </Button>
                  </HStack>
                </Flex>
              </Box>

              {/* Filters and Search */}
              <Box
                bg={cardBg}
                borderRadius="xl"
                p={6}
                mb={6}
                shadow="sm"
                border="1px solid"
                borderColor={borderColor}
              >
                <VStack spacing={4}>
                  <Flex justify="space-between" align="center" w="full" wrap="wrap" gap={4}>
                    <HStack spacing={3}>
                      <Icon as={FaFilter} color="blue.500" />
                      <Text fontSize="lg" fontWeight="600" color="gray.700">
                        {t('filter')} {t('search')}
                      </Text>
                    </HStack>
                  
                  </Flex>
                  
                  <VStack spacing={4} w="full">
                    <Flex gap={4} wrap="wrap" w="full">
                      
                      <Select
                        maxW={isMobile ? "full" : "250px"}
                        value={statusFilter}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          setStatusFilter(newStatus);
                          // Reset to page 1 when status filter changes
                          const url = new URL(location.pathname + location.search, window.location.origin);
                          url.searchParams.set('page', '1');
                          
                          // Update API state parameter based on status filter
                          if (newStatus === 'all') {
                            url.searchParams.delete('state');
                          } else if (newStatus === 'new') {
                            // Map 'new' to both 'new' and 'assignment' states
                            url.searchParams.set('state', 'new,assignment');
                          } else if (newStatus === 'in_progress') {
                            // Map 'in_progress' to multiple states
                            url.searchParams.set('state', 'in_progress,in_queue,eval_started');
                          } else if (newStatus === 'assigned') {
                            // For assigned, we need to exclude new/assignment states
                            // This is tricky with API, so we'll handle it differently
                            url.searchParams.set('state', 'in_progress,in_queue,eval_started,approval,pending_for_coverage,customer_accepted,customer_rejected,sent_to_base,sent_to_reseller,received_by_base,received_by_reseller');
                          } else {
                            url.searchParams.set('state', newStatus);
                          }
                          navigate(url.pathname + '?' + url.searchParams.toString(), { replace: true });
                          try { window.localStorage.setItem('lastTicketFilters', JSON.stringify({ status: newStatus, kind: kindFilter, priority: priorityFilter, city: cityFilter, district: districtFilter })); } catch (_) {}
                        }}
                        bg="white"
                        border="2px solid"
                        borderColor="gray.200"
                      >
                        <option value="all">Tüm Durumlar</option>
                        <option value="new">Yeni</option>
                        <option value="assigned">Atanmış</option>
                        <option value="in_progress">Devam Ediyor</option>
                        <option value="completed">Tamamlandı</option>
                        <option value="cancelled">İptal Edildi</option>
                      </Select>

                      <Select
                        maxW={isMobile ? "full" : "200px"}
                        value={kindFilter}
                        onChange={(e) => {
                          const v = e.target.value;
                          setKindFilter(v);
                          const url = new URL(location.pathname + location.search, window.location.origin);
                          url.searchParams.set('page', '1');
                          if (v) url.searchParams.set('kind', v); else url.searchParams.delete('kind');
                          navigate(url.pathname + '?' + url.searchParams.toString(), { replace: true });
                          try { window.localStorage.setItem('lastTicketFilters', JSON.stringify({ status: statusFilter, kind: v, priority: priorityFilter, city: cityFilter, district: districtFilter })); } catch (_) {}
                        }}
                        bg="white"
                        border="2px solid"
                        borderColor="gray.200"
                      >
                        <option value="">Tür</option>
                        <option value="malfunction">Arıza</option>
                        <option value="request">Talep</option>
                        <option value="education">Eğitim</option>
                        <option value="assembly">Montaj</option>
                        <option value="initialization">Başlatım</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="other">Diğer</option>
                      </Select>

                      <Select
                        maxW={isMobile ? "full" : "180px"}
                        value={priorityFilter}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPriorityFilter(v);
                          const url = new URL(location.pathname + location.search, window.location.origin);
                          url.searchParams.set('page', '1');
                          if (v) url.searchParams.set('priority', v); else url.searchParams.delete('priority');
                          navigate(url.pathname + '?' + url.searchParams.toString(), { replace: true });
                          try { window.localStorage.setItem('lastTicketFilters', JSON.stringify({ status: statusFilter, kind: kindFilter, priority: v, city: cityFilter, district: districtFilter })); } catch (_) {}
                        }}
                        bg="white"
                        border="2px solid"
                        borderColor="gray.200"
                      >
                        <option value="">Öncelik</option>
                        <option value="low">Düşük</option>
                        <option value="medium">Orta</option>
                        <option value="high">Yüksek</option>
                        <option value="urgent">Acil</option>
                      </Select>

                      <Select
                        maxW={isMobile ? "full" : "220px"}
                        value={cityFilter}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCityFilter(v);
                          const url = new URL(location.pathname + location.search, window.location.origin);
                          url.searchParams.set('page', '1');
                          if (v) url.searchParams.set('city', v); else url.searchParams.delete('city');
                          url.searchParams.delete('district');
                          navigate(url.pathname + '?' + url.searchParams.toString(), { replace: true });
                          try { window.localStorage.setItem('lastTicketFilters', JSON.stringify({ status: statusFilter, kind: kindFilter, priority: priorityFilter, city: v, district: '' })); } catch (_) {}
                        }}
                        bg="white"
                        border="2px solid"
                        borderColor="gray.200"
                      >
                        <option value="">Şehir</option>
                        {cities.map(c => (
                          <option key={c.id || c.pk} value={(c.id || c.pk)}>{c.name}</option>
                        ))}
                      </Select>

                      <Select
                        maxW={isMobile ? "full" : "220px"}
                        value={districtFilter}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDistrictFilter(v);
                          const url = new URL(location.pathname + location.search, window.location.origin);
                          url.searchParams.set('page', '1');
                          if (v) url.searchParams.set('district', v); else url.searchParams.delete('district');
                          navigate(url.pathname + '?' + url.searchParams.toString(), { replace: true });
                          try { window.localStorage.setItem('lastTicketFilters', JSON.stringify({ status: statusFilter, kind: kindFilter, priority: priorityFilter, city: cityFilter, district: v })); } catch (_) {}
                        }}
                        bg="white"
                        border="2px solid"
                        borderColor="gray.200"
                        isDisabled={!cityFilter}
                      >
                        <option value="">İlçe</option>
                        {districts.map(d => (
                          <option key={d.id || d.pk} value={(d.id || d.pk)}>{d.name}</option>
                        ))}
                      </Select>
                      
                     
                    </Flex>
                    
                    {/* Column Visibility Controls - Desktop Only */}
                    
                    
                    
                  </VStack>
                </VStack>
              </Box>

              {/* Content Area */}
               <Box
                 bg={cardBg}
                 borderRadius="xl"
                 shadow="lg"
                 overflow="hidden"
                 border="1px solid"
                 borderColor={borderColor}
               >
               {isMobile ? (
                  <Box p={4}>
                    <SimpleGrid columns={1} spacing={4}>
                      {filteredAndSortedTickets.length > 0 ? (
                        filteredAndSortedTickets.map((ticket) => (
                          <Card key={ticket.id} variant="outline" bg={cardBg}>
                            <CardBody p={4}>
                              <VStack align="stretch" spacing={3}>
                                <Flex justify="space-between" align="start">
                                  <HStack>
                                    <Badge colorScheme="blue" variant="solid" borderRadius="full" px={2}>#{ticket.number}</Badge>
                                    <TicketState state={ticket.state} />
                                  </HStack>
                                  <Text fontSize="xs" color="gray.500">{formatDateTable(ticket.created_at)}</Text>
                                </Flex>
                                
                                <VStack align="start" spacing={1}>
                                  <Link to={`/is-emri/${urlToId(ticket.url)}`}>
                                    <Text fontWeight="bold" fontSize="md" color={mobileCardTitleColor} noOfLines={2}>
                                      {ticket.title}
                                    </Text>
                                  </Link>
                                  <HStack spacing={2}>
                                    <Icon as={FaUser} boxSize={3} color="gray.400" />
                                    <Text fontSize="sm" color={mobileCardSubtitleColor}>
                                      {ticket.full_name || ticket.customer?.name || "Bilinmiyor"}
                                    </Text>
                                  </HStack>
                                </VStack>

                                <Divider />
                                
                                <Flex justify="space-between" align="center">
                                  {ticket.assigned_to ? (
                                    <HStack spacing={2}>
                                      <Avatar size="xs" name={ticket.assigned_to.name} src={ticket.assigned_to.avatar} />
                                      <Text fontSize="xs" fontWeight="500">{ticket.assigned_to.name}</Text>
                                    </HStack>
                                  ) : (
                                    <Text fontSize="xs" color="gray.400">Atanmamış</Text>
                                  )}
                                  
                                  <Link to={`/is-emri/${urlToId(ticket.url)}`}>
                                    <Button size="sm" variant="ghost" rightIcon={<ArrowRightIcon />} colorScheme="blue">
                                      Detay
                                    </Button>
                                  </Link>
                                </Flex>
                              </VStack>
                            </CardBody>
                          </Card>
                        ))
                      ) : (
                         <Box p={8} textAlign="center">
                           <VStack spacing={4}>
                             <Icon as={FaTicketAlt} boxSize={12} color="gray.300" />
                             <Text color="gray.500">{t('customer.no_customers_found')}</Text>
                           </VStack>
                         </Box>
                      )}
                    </SimpleGrid>
                  </Box>
               ) : viewMode === 'table' ? (
                 filteredAndSortedTickets.length > 0 ? (
                   <TableContainer position="relative" zIndex={1} overflowX="auto" maxW="100%">
                     <Table variant="simple" size={isMobile ? "sm" : "md"} minW={isMobile ? "800px" : "auto"}>
                       <Thead bg={tableBg}>
                        <Tr>
                          {columns.map((column, index) => (
                            <Th
                              key={column.key}
                              width={column.width}
                              minW={isMobile ? "80px" : column.width}
                              color="gray.600"
                              fontSize={isMobile ? "xs" : "sm"}
                              fontWeight="bold"
                              textTransform="uppercase"
                              letterSpacing="wider"
                              py={isMobile ? 4 : 6}
                              px={isMobile ? 2 : 4}
                              borderBottom="none"
                              position="relative"
                              cursor={column.sortable ? "pointer" : "default"}
                              _hover={column.sortable ? { bg: "gray.100" } : {}}
                              onClick={() => column.sortable && handleSort(column.key)}
                              _after={index < columns.length - 1 ? {
                                content: '""',
                                position: "absolute",
                                right: 0,
                                top: "20%",
                                bottom: "20%",
                                width: "1px",
                                bg: "rgba(139, 92, 246, 0.2)"
                              } : {}}
                            >
                              <HStack spacing={isMobile ? 1 : 2}>
                                <Text>{column.label}</Text>
                                {column.sortable && sortField === column.key && (
                                  <Icon 
                                    as={sortDirection === "asc" ? TriangleUpIcon : TriangleDownIcon} 
                                    boxSize={isMobile ? 2 : 3}
                                    color="blue.500"
                                  />
                                )}
                              </HStack>
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredAndSortedTickets.map((ticket, index) => (
                          <Tr
                            key={ticket.id}
                            _hover={prefersReducedMotion ? { bg: hoverBg } : {
                              bg: hoverBg,
                              transform: "translateY(-1px)",
                              shadow: "md"
                            }}
                            transition="all 0.2s ease"
                            cursor="pointer"
                            onClick={() => window.location.href = `/is-emri/${urlToId(ticket.url)}`}
                          >
                            {visibleColumns.id && (
                              <Td fontWeight="bold" color="blue.600" px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
                                 <HStack spacing={isMobile ? 1 : 2}>
                                   <Text fontSize={isMobile ? "xs" : "sm"}>#{ticket.number}</Text>
                                 </HStack>
                               </Td>
                            )}
                            {visibleColumns.title && (
                              <Td maxW={isMobile ? "150px" : "300px"} px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
                                <VStack align="start" spacing={1}>
                                  <Link to={`/is-emri/${urlToId(ticket.url)}`}>
                                    <Text fontWeight="600" color="gray.800" _hover={{ color: "blue.600" }} fontSize={isMobile ? "xs" : "sm"}>
                                      <ShortenedText text={ticket.title} maxLength={isMobile ? 20 : 40} />
                                    </Text>
                                  </Link>
                                  {ticket.body && !isMobile && (
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                      <ShortenedText text={ticket.body} maxLength={50} />
                                    </Text>
                                  )}
                                </VStack>
                              </Td>
                            )}
                            {visibleColumns.customer && (
                              <Td px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
                                <HStack spacing={isMobile ? 2 : 3}>
                                  {!lowDataMode && (<Avatar size={isMobile ? "xs" : "sm"} name={ticket.full_name || ticket.customer?.name || "Unknown"} bg="blue.500" />)}
                                  {!isMobile && (
                                    <VStack align="start" spacing={0}>
                                      <Text fontWeight="500" fontSize="sm">
                                        {ticket.full_name || ticket.customer?.name || "Bilinmiyor"}
                                      </Text>
                                      {ticket.customer?.email && (
                                        <Text fontSize="xs" color="gray.500">
                                          {ticket.customer.email}
                                        </Text>
                                      )}
                                    </VStack>
                                  )}
                                  {isMobile && (
                                    <Text fontWeight="500" fontSize="xs" noOfLines={1}>
                                      {ticket.full_name || ticket.customer?.name || "Bilinmiyor"}
                                    </Text>
                                  )}
                                </HStack>
                              </Td>
                            )}
                            
                            {visibleColumns.state && (
                              <Td px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
                                <VStack align="start" spacing={1}>
                                 
                                    <TicketState state={ticket.state} />
                                  
                                  {ticket.assigned_to ? (
                                    <Link to={`/bayi/${urlToId(ticket.assigned_to.url)}`}>
                                      <Badge
                                        colorScheme="gray"
                                        variant="subtle"
                                        borderRadius="full"
                                        px={isMobile ? 2 : 3}
                                        py={1}
                                        fontSize={isMobile ? "2xs" : "xs"}
                                        fontWeight="bold"
                                      >
                                        {ticket.assigned_to.name}
                                      </Badge>
                                    </Link>
                                  ) : (
                                    <Text>
                                    </Text>
                                  )}
                                </VStack>
                              </Td>
                            )}
                            {visibleColumns.reseller && (
                              <Td px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
                                <HStack spacing={isMobile ? 2 : 3}>
                                  {ticket.assigned_to ? (
                                    <Link to={`/bayi/${urlToId(ticket.assigned_to.url)}`}>
                                      <Text fontSize={isMobile ? "xs" : "sm"} fontWeight="500" color="gray.800" _hover={{ color: "blue.600" }}>
                                        {ticket.assigned_to.name}
                                      </Text>
                                    </Link>
                                  ) : (
                                    <Text fontSize={isMobile ? "xs" : "sm"} color="gray.500">Atanmamış</Text>
                                  )}
                                </HStack>
                              </Td>
                            )}
                            {visibleColumns.created_at && (
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Tooltip label={formatDate(ticket.created_at)}>
                                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                                      {formatDateTable(ticket.created_at)}
                                    </Text>
                                  </Tooltip>
                                  <HStack spacing={1}>
                                    <Icon as={FaCalendarAlt} boxSize={3} color="gray.400" />
                                    <Text fontSize="xs" color="gray.500">
                                      {Math.floor((new Date() - new Date(ticket.created_at)) / (1000 * 60 * 60 * 24))} {t('ticket.timeline.created_desc').split(' ')[0] || 'gün'} {t('ticket.timeline.created_desc').includes('tarafından') ? '' : 'önce'}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </Td>
                            )}
                            {visibleColumns.updated_at && (
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Tooltip label={formatDate(ticket.updated_at)}>
                                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                                      {formatDateTable(ticket.updated_at)}
                                    </Text>
                                  </Tooltip>
                                  <HStack spacing={1}>
                                    <Icon as={FaClock} boxSize={3} color="gray.400" />
                                    <Text fontSize="xs" color="gray.500">
                                      {Math.floor((new Date() - new Date(ticket.updated_at)) / (1000 * 60 * 60))} {t('ticket.timeline.current_status').includes('Current') ? 'hours' : 'saat'} {t('ticket.timeline.current_status').includes('Current') ? 'ago' : 'önce'}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </Td>
                            )}
                            {visibleColumns.actions && (
                              <Td onClick={(e) => e.stopPropagation()}>
                              <VStack spacing={2}>
                                <Link to={`/dashboard/ticket/${urlToId(ticket.url)}`}>
                                  <Button
                                   size="sm"
                                   colorScheme="blue"
                                   variant="solid"
                                   rightIcon={<ArrowRightIcon />}
                                   _hover={{
                                     transform: "translateY(-1px)",
                                     shadow: "md"
                                   }}
                                 >
                                    {t('customer.view_details')}
                                  </Button>
                                </Link>
                                {ticket.tasks_count > 0 && (
                                  <Text fontSize="xs" color="blue.500" fontWeight="500">
                                    {ticket.tasks_count} {t('ticket.tasks.title')}
                                  </Text>
                                )}
                              </VStack>
                            </Td>
                            )}
                          </Tr>
                        ))}
                      </Tbody>
                     </Table>
                   </TableContainer>
                 ) : (
                   <Box p={12} textAlign="center">
                     <VStack spacing={6}>
                       <Icon as={FaTicketAlt} boxSize={16} color="gray.300" />
                       <Text fontSize="xl" color="gray.500" fontWeight="600">
                         {searchTerm || statusFilter !== "all" 
                           ? t('customer.no_customers_found') 
                           : t('customer.no_customers_yet')
                         }
                       </Text>
                       <Text color="gray.400">
                         {searchTerm || statusFilter !== "all" 
                           ? t('customer.try_different_search') 
                           : t('tickets.create_new_service_request')
                         }
                       </Text>
                       {(searchTerm || statusFilter !== "all") && (
                         <Button
                           colorScheme="blue"
                           variant="outline"
                           onClick={() => {
                             setSearchTerm("");
                             setStatusFilter("all");
                             const url = new URL(window.location.href);
                             url.searchParams.delete('number');
                             url.searchParams.delete('q');
                             url.searchParams.delete('state');
                             url.searchParams.set('page', '1');
                             window.history.replaceState({}, '', url.toString());
                             window.location.reload();
                           }}
                         >
                           {t('common.clear_filters')}
                         </Button>
                       )}
                     </VStack>
                   </Box>
                 )
               ) : (
                 <Flex gap={4} wrap="wrap" p={4}>
                   {['new','assignment','in_queue','in_progress','approval','completed','cancelled'].map((st) => (
                     <Box key={st} minW={{base:'100%', md:'300px'}} flex={1} bg={tableBg} borderRadius="lg" p={4}>
                       <HStack mb={3}>
                         <Badge colorScheme="blue">{translateTableBadge(st)}</Badge>
                         <Text fontWeight="600">{filteredAndSortedTickets.filter(t=>t.state===st).length}</Text>
                       </HStack>
                       <VStack align="stretch" spacing={3} maxH="70vh" overflowY="auto">
                         {filteredAndSortedTickets.filter(t=>t.state===st).map((t) => (
                           <Box key={t.url} border="1px solid" borderColor={borderColor} borderRadius="md" p={3} bg={cardBg} _hover={{shadow:'md'}}>
                             <Link to={`/is-emri/${urlToId(t.url)}`}><Text fontWeight="600">#{t.number} {t.title}</Text></Link>
                             <Text fontSize="sm" color="gray.600">{t.full_name || t.customer?.name || '-'}</Text>
                             <HStack spacing={2} mt={1}>
                               <Badge colorScheme={getStatusColor(t.state)}>{t.state}</Badge>
                               <Text fontSize="xs" color="gray.500">{formatDate(t.created_at)}</Text>
                             </HStack>
                           </Box>
                         ))}
                       </VStack>
                     </Box>
                   ))}
                 </Flex>
               )}

                {/* Pagination and Stats */}
                 {filteredAndSortedTickets.length > 0 && (
                   <Box p={6} borderTop="1px solid" borderColor={borderColor}>
                     <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                       <VStack align="start" spacing={1}>
                         <Text fontSize="sm" color="gray.600" fontWeight="500">
                           {t('total_tickets')}: {filteredAndSortedTickets.length}
                         </Text>
                         <HStack spacing={4} fontSize="xs" color="gray.500">
                           <Text>{t('status.new')}: {filteredAndSortedTickets.filter(t => t.state === 'new').length}</Text>
                           <Text>{t('states.in_progress') || 'Devam Ediyor'}: {filteredAndSortedTickets.filter(t => t.state === 'in_progress').length}</Text>
                           <Text>{t('states.completed') || 'Tamamlandı'}: {filteredAndSortedTickets.filter(t => t.state === 'completed').length}</Text>
                         </HStack>
                       </VStack>
                       <Pagination
                         path={"is-emri"}
                         num_of_items={tickets?.num_of_items || 0}
                         num_of_pages={tickets?.num_of_pages || 1}
                         currentPage={tickets?.current_page || 1}
                       />
                     </Flex>
                   </Box>
                 )}
              </Box>
            </VStack>
          </Container>
         </Box>
       </Box>
     );
  }
